/**
 * Places Autocomplete helpers for hierarchical location search.
 *
 * Request has NO type restriction so street addresses work (VN house numbers,
 * "1N đường Núi Thành", …). Keep named places as well as administrative areas;
 * activity eligibility is validated when a location is selected.
 */

const ADDRESS_TYPES = new Set(['street_address', 'route', 'premise', 'subpremise', 'geocode']);

const SPECIFIC_ADDRESS_TYPES = new Set(['street_address', 'route', 'premise', 'subpremise']);

/**
 * Normalize for accent-insensitive match (Da Nang ↔ Đà Nẵng).
 */
export function normalizePlaceQuery(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * How far from the member's city a suggestion still counts as « near ».
 * The same 50 km the Explorer looks around by default.
 */
export const NEAR_THE_MEMBERS_CITY_METERS = 50_000;

/**
 * Unrestricted autocomplete (places + cities + regions + street addresses).
 * Type restrictions can hide named venues and VN administrative addresses.
 *
 * `near` is the member's city. Without it, Google ranked « Pâquier » in the
 * Aosta Valley above « Le Pâquier » in Annecy for a member of Annecy, and an
 * Annecy social run was saved 116 km away (Sofia, B15, 28/10). The bias asks
 * Google to prefer what is close; the origin makes it return each
 * suggestion's distance, which the sort below reads.
 */
export function buildGeoAutocompleteRequest(
  input: string,
  language: string,
  near?: { lat: number; lng: number },
): google.maps.places.AutocompletionRequest {
  if (!near) {
    return {
      input,
      language,
    };
  }
  return {
    input,
    language,
    origin: near,
    locationBias: { center: near, radius: NEAR_THE_MEMBERS_CITY_METERS },
  };
}

/**
 * The city place search looks around: the one on the member's profile, else
 * the one the Explorer searches around. Undefined when neither is known, and
 * the search then stays as it was, worldwide and unbiased.
 */
export function theCityToSearchAround(
  profilePosition: PositionToSearchAround,
  explorePosition: PositionToSearchAround,
): { lat: number; lng: number } | undefined {
  return asLatLng(profilePosition) ?? asLatLng(explorePosition);
}

type PositionToSearchAround = { lat?: number | null; lng?: number | null } | undefined | null;

// A stored Explorer position can carry `lon` rather than `lng`, as the place
// line of the Explorer already allows for. Google takes neither half alone.
function asLatLng(position: PositionToSearchAround): { lat: number; lng: number } | undefined {
  const lat = position?.lat;
  const lng = position?.lng ?? (position as { lon?: number } | null | undefined)?.lon;
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return undefined;
  }
  return { lat, lng };
}

function isNearTheMembersCity(prediction: google.maps.places.AutocompletePrediction): boolean {
  return typeof prediction.distance_meters === 'number' && prediction.distance_meters <= NEAR_THE_MEMBERS_CITY_METERS;
}

function hasType(types: string[], allowed: Set<string>): boolean {
  return types.some((type) => allowed.has(type));
}

/**
 * Streets and named POIs can display the provider's description immediately.
 * Administrative and generic geocode predictions still need hierarchy details.
 * This controls presentation only, never selection eligibility.
 */
export function isSpecificAddressPrediction(prediction: google.maps.places.AutocompletePrediction): boolean {
  const types = prediction.types || [];
  if (hasType(types, SPECIFIC_ADDRESS_TYPES)) {
    return true;
  }
  const isAdministrative = types.some(
    (type) =>
      ['political', 'locality', 'postal_town', 'country'].includes(type) ||
      type.startsWith('administrative_area_level_') ||
      type.startsWith('sublocality'),
  );
  return !isAdministrative && (types.includes('establishment') || types.includes('point_of_interest'));
}

function predictionPrimaryName(prediction: google.maps.places.AutocompletePrediction): string {
  return prediction.structured_formatting?.main_text || prediction.description.split(',')[0] || prediction.description;
}

function matchScore(name: string, query: string): number {
  if (!query) {
    return 0;
  }
  if (name === query) {
    return 100;
  }
  if (name.startsWith(query)) {
    return 80;
  }
  const words = name.split(/\s+/);
  if (words.some((word) => word.startsWith(query))) {
    return 60;
  }
  if (name.includes(query)) {
    return 40;
  }
  return 0;
}

function adminRank(prediction: google.maps.places.AutocompletePrediction, preferAddress: boolean): number {
  const types = prediction.types || [];
  if (preferAddress) {
    if (hasType(types, ADDRESS_TYPES)) {
      return 50;
    }
  }
  if (types.includes('country')) {
    return 45;
  }
  if (types.includes('locality') || types.includes('postal_town') || types.includes('administrative_area_level_1')) {
    return 40;
  }
  if (types.includes('administrative_area_level_2')) {
    return 20;
  }
  if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
    return 10;
  }
  if (hasType(types, ADDRESS_TYPES)) {
    return 25;
  }
  return 15;
}

function looksLikeStreetQuery(input: string): boolean {
  return /\d/.test(input) || /đường|duong|street|rue|avenue|phố|pho/i.test(input);
}

/**
 * Rank provider suggestions without excluding places by category.
 * What lies near the member's city comes first. Then street-like queries
 * prefer addresses; city queries prefer admin matches.
 */
export function filterAndSortGeoPredictions(
  predictions: google.maps.places.AutocompletePrediction[],
  input: string,
): google.maps.places.AutocompletePrediction[] {
  const query = normalizePlaceQuery(input);
  const preferAddress = looksLikeStreetQuery(input);
  return predictions.slice().sort((left, right) => {
    const leftMatch = matchScore(normalizePlaceQuery(predictionPrimaryName(left)), query);
    const rightMatch = matchScore(normalizePlaceQuery(predictionPrimaryName(right)), query);
    // Near first, before how well the name matches: an exact « Pâquier »
    // in Italy used to beat « Le Pâquier » in the member's own city.
    const leftNear = isNearTheMembersCity(left);
    const rightNear = isNearTheMembersCity(right);
    if (leftNear !== rightNear) {
      return leftNear ? -1 : 1;
    }
    if (leftMatch !== rightMatch) {
      return rightMatch - leftMatch;
    }
    return adminRank(right, preferAddress) - adminRank(left, preferAddress);
  });
}
